import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { format, addDays, startOfMonth, endOfMonth, getDay, getDaysInMonth, subMonths, addMonths, parseISO } from 'date-fns';
import { formatTimeIST } from '@/utils/dateUtils';
import BH0PayrollTab from '@/components/attendance/BH0PayrollTab';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MapPin,
  AlertTriangle,
  ShieldAlert,
  Shield,
  Unlock,
  Timer,
  X,
  Camera,
  FileEdit,
  Check,
  XCircle,
  Loader2,
} from 'lucide-react';

// ─── Status badge component ──────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    present:        { bg: 'bg-green-100 text-green-800 border-green-300', label: 'Present' },
    half_day:       { bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Half Day' },
    absent:         { bg: 'bg-red-100 text-red-800 border-red-300', label: 'Absent' },
    weekly_off:     { bg: 'bg-gray-100 text-gray-600 border-gray-300', label: 'Weekly Off' },
    not_checked_in: { bg: 'bg-gray-100 text-gray-500 border-gray-300', label: 'Not Checked In' },
    checked_in:     { bg: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Checked In' },
    late:           { bg: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Late' },
  };
  const c = config[status] || { bg: 'bg-gray-100 text-gray-800', label: status || 'Unknown' };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${c.bg}`}>
      {c.label}
    </span>
  );
};

// ─── Summary stat card ───────────────────────────────────────────────────
const StatCard = ({ label, value, color, icon: Icon }) => {
  const colors = {
    green:  'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red:    'bg-red-50 border-red-200 text-red-700',
    gray:   'bg-gray-50 border-gray-200 text-gray-600',
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colors[color] || colors.gray}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        {Icon && <Icon className="h-8 w-8 opacity-40" />}
      </div>
    </div>
  );
};

// ─── Calendar heatmap (personal view) ────────────────────────────────────
const CalendarHeatmap = ({ year, month, records, onDayClick }) => {
  const firstDay = startOfMonth(new Date(year, month, 1));
  const totalDays = getDaysInMonth(firstDay);
  const startWeekday = getDay(firstDay); // 0=Sun

  const statusColor = (status) => {
    switch (status) {
      case 'present':    return 'bg-green-500 text-white hover:bg-green-600';
      case 'half_day':   return 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500';
      case 'absent':     return 'bg-red-500 text-white hover:bg-red-600';
      case 'weekly_off': return 'bg-gray-300 text-gray-600 hover:bg-gray-400';
      case 'late':       return 'bg-amber-400 text-amber-900 hover:bg-amber-500';
      case 'checked_in': return 'bg-blue-400 text-white hover:bg-blue-500';
      default:           return 'bg-gray-100 text-gray-400';
    }
  };

  // Map records by day number
  const dayMap = {};
  (records || []).forEach(r => {
    const d = typeof r.date === 'string' ? parseISO(r.date) : r.date;
    const day = d.getDate();
    const m = d.getMonth();
    const y = d.getFullYear();
    if (y === year && m === month) {
      dayMap[day] = r;
    }
  });

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const cells = [];

  // Empty cells before first day
  for (let i = 0; i < startWeekday; i++) {
    cells.push(<div key={`empty-${i}`} className="w-10 h-10" />);
  }

  for (let day = 1; day <= totalDays; day++) {
    const rec = dayMap[day];
    const status = rec?.status;
    const isToday = new Date().getFullYear() === year && new Date().getMonth() === month && new Date().getDate() === day;

    cells.push(
      <button
        key={day}
        onClick={() => onDayClick(day, rec)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors cursor-pointer
          ${statusColor(status)}
          ${isToday ? 'ring-2 ring-[#7C1D3E] ring-offset-1' : ''}
        `}
        title={status ? `${status.replace('_', ' ')}` : 'No data'}
      >
        {day}
      </button>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(d => (
          <div key={d} className="w-10 h-8 flex items-center justify-center text-xs font-medium text-gray-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells}
      </div>
    </div>
  );
};

// ─── Day detail modal ────────────────────────────────────────────────────
const DayDetailModal = ({ day, record, onClose }) => {
  if (!day) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Day Details</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>
        {record ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status</span>
              <StatusBadge status={record.status} />
            </div>
            {record.location && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Location</span>
                <span className="text-sm font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {record.location_name || record.location}
                </span>
              </div>
            )}
            {record.check_in_time && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Check-in</span>
                <span className="text-sm font-medium">{formatTimeIST(record.check_in_time) || '-'}</span>
              </div>
            )}
            {record.check_out_time && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Check-out</span>
                <span className="text-sm font-medium">{formatTimeIST(record.check_out_time) || '-'}</span>
              </div>
            )}
            {record.hours_worked != null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Hours Worked</span>
                <span className={`text-sm font-bold ${record.hours_worked < 6 ? 'text-red-600' : 'text-green-700'}`}>
                  {record.hours_worked}h
                </span>
              </div>
            )}
            {record.justification && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs font-medium text-yellow-800 mb-1">Justification</p>
                <p className="text-sm text-yellow-700">{record.justification}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No attendance data for this day.</p>
        )}
      </div>
    </div>
  );
};

// ─── Unblock confirmation dialog ─────────────────────────────────────────
const UnblockDialog = ({ user: targetUser, onConfirm, onCancel }) => {
  if (!targetUser) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-full">
            <Unlock className="h-5 w-5 text-amber-700" />
          </div>
          <h3 className="text-lg font-bold">Unblock User</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to unblock <strong>{targetUser.user_name}</strong>?
          This will reset their strike count and allow them to check in again.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} className="bg-[#7C1D3E] hover:bg-[#6a1835] text-white">
            Confirm Unblock
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Month names helper ──────────────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ═════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════
export default function AttendanceDashboard() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role;
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isTeamView = isAdmin || isManager;

  // ── Admin tab state (team vs bh0 payroll) ──
  const [adminTab, setAdminTab] = useState('team'); // 'team' | 'bh0'
  const bh0TabRefreshRef = useRef(null);

  // ── Team view state (admin/manager) ──
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState(null);
  const [checkinTypeFilter, setCheckinTypeFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [unblockTarget, setUnblockTarget] = useState(null);

  // ── Regularization review state (admin/manager) ──
  const [pendingRegs, setPendingRegs] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regActionLoading, setRegActionLoading] = useState(null); // id being acted on
  const [rejectingId, setRejectingId] = useState(null); // id with reject reason input open
  const [rejectReason, setRejectReason] = useState('');
  const [regPhotoPreview, setRegPhotoPreview] = useState(null); // photo URL to preview

  // ── BH0 state (manager/admin) ──
  const [bh0MarkModalOpen, setBh0MarkModalOpen] = useState(false);
  const [bh0MarkTarget, setBh0MarkTarget] = useState(null); // {user_id, user_name}
  const [bh0MarkDates, setBh0MarkDates] = useState([]);
  const [bh0MarkReason, setBh0MarkReason] = useState('');
  const [bh0MarkLoading, setBh0MarkLoading] = useState(false);
  const [bh0DateInput, setBh0DateInput] = useState(''); // for date picker input
  // Note: BH0 count per user in team table would require a dedicated backend endpoint.
  // For now, the "Mark BH0" button is the primary interaction point.

  // ── BH0 appeals state (admin only) ──
  const [bh0Appeals, setBh0Appeals] = useState([]);
  const [bh0AppealsLoading, setBh0AppealsLoading] = useState(false);
  const [bh0ResolvingId, setBh0ResolvingId] = useState(null); // id being resolved
  const [bh0ResolveDecision, setBh0ResolveDecision] = useState(null); // 'uphold' or 'reverse'
  const [bh0ResolveNotes, setBh0ResolveNotes] = useState('');
  const [bh0ResolveLoading, setBh0ResolveLoading] = useState(false);

  // ── Personal calendar state ──
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [myHistory, setMyHistory] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayRecord, setSelectedDayRecord] = useState(null);

  // ── Format time helper ──
  const formatTime = (isoString) => formatTimeIST(isoString) || '-';

  // ── Role display helper ──
  const getRoleDisplay = (r) => {
    const names = { team_leader: 'TL', fitness_manager: 'FM', manager: 'Manager', admin: 'Admin' };
    return names[r] || r;
  };

  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');

  // ── Fetch team overview (admin/manager) ──
  const fetchTeamOverview = useCallback(async () => {
    if (!isTeamView) return;
    setTeamLoading(true);
    try {
      const params = { date: selectedDate };
      if (managerFilter !== 'all') params.manager_id = managerFilter;
      const response = await axiosInstance.get('/attendance/daily/team-overview', { params });
      setTeamData(response.data);
    } catch (error) {
      console.error('Failed to fetch team overview:', error);
      toast.error('Failed to load team attendance data');
    } finally {
      setTeamLoading(false);
    }
  }, [selectedDate, managerFilter, isTeamView]);

  useEffect(() => {
    fetchTeamOverview();
  }, [fetchTeamOverview]);

  // ── Fetch pending regularization requests (admin/manager) ──
  const fetchPendingRegs = useCallback(async () => {
    if (!isTeamView) return;
    setRegsLoading(true);
    try {
      const res = await axiosInstance.get('/attendance/daily/regularization/pending');
      setPendingRegs(res.data?.requests || res.data || []);
    } catch {
      setPendingRegs([]);
    } finally {
      setRegsLoading(false);
    }
  }, [isTeamView]);

  useEffect(() => {
    fetchPendingRegs();
  }, [fetchPendingRegs]);

  // ── Approve regularization ──
  const handleRegApprove = async (id) => {
    setRegActionLoading(id);
    try {
      await axiosInstance.put(`/attendance/daily/regularization/${id}/approve`);
      toast.success('Regularization request approved');
      fetchPendingRegs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    } finally {
      setRegActionLoading(null);
    }
  };

  // ── Reject regularization ──
  const handleRegReject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setRegActionLoading(id);
    try {
      await axiosInstance.put(`/attendance/daily/regularization/${id}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success('Regularization request rejected');
      setRejectingId(null);
      setRejectReason('');
      fetchPendingRegs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRegActionLoading(null);
    }
  };

  // ── Fetch personal history (calendar) ──
  const fetchMyHistory = useCallback(async () => {
    setMyLoading(true);
    try {
      const from = format(startOfMonth(new Date(calYear, calMonth, 1)), 'yyyy-MM-dd');
      const to = format(endOfMonth(new Date(calYear, calMonth, 1)), 'yyyy-MM-dd');
      const response = await axiosInstance.get('/attendance/daily/my-history', {
        params: { from_date: from, to_date: to }
      });
      setMyHistory(Array.isArray(response.data) ? response.data : response.data?.entries || []);
    } catch (error) {
      console.error('Failed to fetch personal history:', error);
      toast.error('Failed to load your attendance history');
    } finally {
      setMyLoading(false);
    }
  }, [calMonth, calYear]);

  useEffect(() => {
    fetchMyHistory();
  }, [fetchMyHistory]);

  // ── Personal summary stats ──
  const personalStats = useMemo(() => {
    const stats = { present: 0, half_day: 0, absent: 0, weekly_off: 0, total_hours: 0 };
    (myHistory || []).forEach(r => {
      if (r.status === 'present' || r.status === 'checked_in') stats.present++;
      else if (r.status === 'half_day') stats.half_day++;
      else if (r.status === 'absent') stats.absent++;
      else if (r.status === 'weekly_off') stats.weekly_off++;
      if (r.hours_worked) stats.total_hours += parseFloat(r.hours_worked) || 0;
    });
    stats.total_hours = Math.round(stats.total_hours * 10) / 10;
    return stats;
  }, [myHistory]);

  // ── Team filtered records ──
  const filteredRecords = useMemo(() => {
    if (!teamData?.team && !teamData?.records && !teamData?.attendance_records) return [];
    const records = teamData.team || teamData.records || teamData.attendance_records || [];
    return records.filter(record => {
      const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
      const matchesRole = roleFilter === 'all' || (record.user_role || record.role) === roleFilter;
      return matchesStatus && matchesRole;
    });
  }, [teamData, statusFilter, roleFilter]);

  // ── Team summary ──
  const teamSummary = useMemo(() => {
    if (!teamData) return { present: 0, absent: 0, half_day: 0, not_checked_in: 0, total: 0 };
    return {
      present: teamData.present || teamData.summary?.present || 0,
      absent: teamData.absent || teamData.summary?.absent || 0,
      half_day: teamData.half_day || teamData.summary?.half_day || 0,
      not_checked_in: teamData.not_checked_in || teamData.summary?.not_checked_in || 0,
      total: teamData.total || teamData.summary?.total || filteredRecords.length,
    };
  }, [teamData, filteredRecords]);

  // ── Date navigation ──
  const goToPreviousDay = () => setSelectedDate(format(addDays(new Date(selectedDate), -1), 'yyyy-MM-dd'));
  const goToNextDay = () => setSelectedDate(format(addDays(new Date(selectedDate), 1), 'yyyy-MM-dd'));
  const goToToday = () => setSelectedDate(format(new Date(), 'yyyy-MM-dd'));

  // ── Month navigation ──
  const goToPrevMonth = () => {
    const prev = subMonths(new Date(calYear, calMonth, 1), 1);
    setCalMonth(prev.getMonth());
    setCalYear(prev.getFullYear());
  };
  const goToNextMonth = () => {
    const next = addMonths(new Date(calYear, calMonth, 1), 1);
    setCalMonth(next.getMonth());
    setCalYear(next.getFullYear());
  };

  // ── Calendar day click handler ──
  const handleDayClick = (day, record) => {
    setSelectedDay(day);
    setSelectedDayRecord(record || null);
  };

  // ── Unblock handler (admin only) ──
  const handleUnblock = async () => {
    if (!unblockTarget) return;
    try {
      await axiosInstance.put(`/attendance/daily/admin/unblock/${unblockTarget.user_id}`);
      toast.success(`${unblockTarget.user_name} has been unblocked`);
      setUnblockTarget(null);
      fetchTeamOverview();
    } catch (error) {
      console.error('Unblock failed:', error);
      toast.error('Failed to unblock user');
    }
  };

  // ── Fetch BH0 pending appeals (admin only) ──
  const fetchBh0Appeals = useCallback(async () => {
    if (!isAdmin) return;
    setBh0AppealsLoading(true);
    try {
      const res = await axiosInstance.get('/attendance/daily/bh0/pending-appeals');
      setBh0Appeals(res.data?.appeals || []);
    } catch {
      setBh0Appeals([]);
    } finally {
      setBh0AppealsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchBh0Appeals();
  }, [fetchBh0Appeals]);

  // ── Mark BH0 handler ──
  const handleBh0Mark = async () => {
    if (!bh0MarkTarget || bh0MarkDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }
    if (bh0MarkReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setBh0MarkLoading(true);
    try {
      const res = await axiosInstance.post('/attendance/daily/bh0/mark', {
        user_id: bh0MarkTarget.user_id,
        dates: bh0MarkDates,
        reason: bh0MarkReason.trim(),
      });
      const msg = res.data?.message || 'BH0 marked successfully';
      const skipped = res.data?.skipped_dates || [];
      if (skipped.length > 0) {
        toast.success(`${msg}. Skipped (already marked): ${skipped.join(', ')}`);
      } else {
        toast.success(msg);
      }
      setBh0MarkModalOpen(false);
      setBh0MarkTarget(null);
      setBh0MarkDates([]);
      setBh0MarkReason('');
      setBh0DateInput('');
      fetchTeamOverview();
      if (bh0TabRefreshRef.current) bh0TabRefreshRef.current();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark BH0');
    } finally {
      setBh0MarkLoading(false);
    }
  };

  // ── Add date to BH0 mark list ──
  const addBh0Date = (dateStr) => {
    if (!dateStr) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    if (dateStr > today) {
      toast.error('Cannot mark future dates as BH0');
      return;
    }
    if (bh0MarkDates.includes(dateStr)) {
      toast.error('Date already added');
      return;
    }
    setBh0MarkDates((prev) => [...prev, dateStr].sort());
    setBh0DateInput('');
  };

  // ── Remove date from BH0 mark list ──
  const removeBh0Date = (dateStr) => {
    setBh0MarkDates((prev) => prev.filter((d) => d !== dateStr));
  };

  // ── Resolve BH0 appeal (admin) ──
  const handleBh0ResolveAppeal = async (bh0Id, decision) => {
    setBh0ResolveLoading(true);
    try {
      await axiosInstance.put(`/attendance/daily/bh0/${bh0Id}/resolve-appeal`, {
        decision,
        notes: bh0ResolveNotes.trim(),
      });
      toast.success(`Appeal ${decision === 'reverse' ? 'reversed' : 'upheld'} successfully`);
      setBh0ResolvingId(null);
      setBh0ResolveDecision(null);
      setBh0ResolveNotes('');
      fetchBh0Appeals();
      fetchTeamOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve appeal');
    } finally {
      setBh0ResolveLoading(false);
    }
  };

  // ═════════════════════════════════════════════════════════════════════
  // Render: Personal Calendar View (TL/FM, or bottom section for manager/admin)
  // ═════════════════════════════════════════════════════════════════════
  const renderPersonalCalendar = () => (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#7C1D3E]" />
            My Attendance
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold min-w-[140px] text-center">
              {MONTH_NAMES[calMonth]} {calYear}
            </span>
            <Button variant="outline" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {myLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Summary cards row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <StatCard label="Present" value={personalStats.present} color="green" icon={UserCheck} />
              <StatCard label="Half Day" value={personalStats.half_day} color="yellow" icon={Clock} />
              <StatCard label="Absent" value={personalStats.absent} color="red" icon={UserX} />
              <StatCard label="Weekly Off" value={personalStats.weekly_off} color="gray" icon={Calendar} />
              <StatCard label="Total Hours" value={`${personalStats.total_hours}h`} color="blue" icon={Timer} />
            </div>

            {/* Calendar legend */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded bg-green-500" /> Present
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded bg-yellow-400" /> Half Day
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded bg-red-500" /> Absent
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded bg-gray-300" /> Weekly Off
              </div>
            </div>

            {/* Calendar grid */}
            <div className="flex justify-center">
              <CalendarHeatmap
                year={calYear}
                month={calMonth}
                records={myHistory}
                onDayClick={handleDayClick}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  // ═════════════════════════════════════════════════════════════════════
  // Render: Team Table View (admin/manager)
  // ═════════════════════════════════════════════════════════════════════
  const renderTeamView = () => (
    <>
      {/* Date picker + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-[#7C1D3E]" />
            Attendance Report
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isAdmin ? 'All users attendance overview' : 'Your team attendance overview'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousDay}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40"
          />
          <Button variant="outline" size="icon" onClick={goToNextDay}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button variant="outline" onClick={goToToday}>Today</Button>
          )}
          <Button variant="outline" onClick={fetchTeamOverview} disabled={teamLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${teamLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Team summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Present" value={teamSummary.present} color="green" icon={UserCheck} />
        <StatCard label="Absent" value={teamSummary.absent} color="red" icon={UserX} />
        <StatCard label="Half Day" value={teamSummary.half_day} color="yellow" icon={Clock} />
        <StatCard label="Not Checked In" value={teamSummary.not_checked_in} color="gray" icon={AlertTriangle} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Status:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="present">Present</SelectItem>
              <SelectItem value="half_day">Half Day</SelectItem>
              <SelectItem value="absent">Absent</SelectItem>
              <SelectItem value="not_checked_in">Not Checked In</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Role:</span>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="team_leader">TL</SelectItem>
              <SelectItem value="fitness_manager">FM</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Manager filter (admin only) */}
        {isAdmin && teamData?.managers?.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Manager:</span>
            <Select value={managerFilter} onValueChange={setManagerFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Managers</SelectItem>
                {teamData.managers.map((m) => (
                  <SelectItem key={m.id || m._id} value={m.id || m._id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Location filter */}
        {(() => {
          const locs = [...new Set((filteredRecords || []).map(r => r.location_name).filter(Boolean))];
          return locs.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Location:</span>
              <Select value={locationFilter || 'all'} onValueChange={(v) => setLocationFilter(v === 'all' ? null : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locs.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })()}

        {/* Check-in type filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Type:</span>
          <Select value={checkinTypeFilter} onValueChange={setCheckinTypeFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="home">Home Gym</SelectItem>
              <SelectItem value="visiting">Visiting</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <span className="text-sm text-gray-500 ml-auto">
          Showing {filteredRecords.length} records
        </span>
      </div>

      {/* Team table */}
      <Card>
        <CardContent className="p-0">
          {teamLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No attendance records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-sm">Name</th>
                    <th className="text-left p-3 font-medium text-sm">Role</th>
                    <th className="text-left p-3 font-medium text-sm">Status</th>
                    <th className="text-left p-3 font-medium text-sm">Location</th>
                    <th className="text-center p-3 font-medium text-sm">Type</th>
                    <th className="text-left p-3 font-medium text-sm">Check-in</th>
                    <th className="text-left p-3 font-medium text-sm">Check-out</th>
                    <th className="text-right p-3 font-medium text-sm">Hours</th>
                    <th className="text-center p-3 font-medium text-sm">GPS</th>
                    <th className="text-center p-3 font-medium text-sm">Photo</th>
                    {isAdmin && <th className="text-center p-3 font-medium text-sm">Flags</th>}
                    {isTeamView && <th className="text-center p-3 font-medium text-sm">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords
                    .filter(r => !locationFilter || r.location_name === locationFilter)
                    .filter(r => checkinTypeFilter === 'all' || (checkinTypeFilter === 'home' ? r.is_home_gym === true : r.is_home_gym === false))
                    .map((record, idx) => {
                    const strikes = record.strike_count || record.strikes || 0;
                    const isLocked = strikes >= 3;
                    const hasFlags = record.flagged_for_review || record.gps_fallback || record.photo_fallback_used || record.missed_checkout;
                    return (
                      <tr
                        key={record.user_id || idx}
                        className={`border-b hover:bg-gray-50 transition-colors ${
                          record.status === 'absent' ? 'bg-red-50/50' :
                          record.status === 'half_day' ? 'bg-yellow-50/50' :
                          record.status === 'not_checked_in' ? '' :
                          record.status === 'present' || record.status === 'checked_in' ? 'bg-green-50/30' : ''
                        }`}
                      >
                        <td className="p-3">
                          <span className="font-medium text-sm">{record.user_name || record.name}</span>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                            {getRoleDisplay(record.user_role || record.role)}
                          </span>
                        </td>
                        <td className="p-3">
                          <StatusBadge status={record.status} />
                        </td>
                        <td className="p-3 text-sm">
                          {record.location_name ? (
                            <div className="flex items-center gap-1.5">
                              <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${record.location_type === 'hq' ? 'text-blue-500' : 'text-emerald-500'}`} />
                              <div>
                                <span className="font-medium text-sm leading-tight">{record.location_name}</span>
                                <span className="text-xs text-gray-400">{record.location_type === 'hq' ? 'HQ' : 'Gym'}</span>
                              </div>
                            </div>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 text-center">
                          {record.location_name ? (
                            record.is_home_gym === false
                              ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">Visiting</span>
                              : <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Home</span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 text-sm">
                          {formatTime(record.check_in_time) || <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 text-sm">
                          {formatTime(record.check_out_time) || <span className="text-gray-300">-</span>}
                        </td>
                        <td className="p-3 text-sm text-right">
                          {record.hours_worked != null ? (
                            <span className={`font-medium ${record.hours_worked < 6 ? 'text-red-600' : record.hours_worked >= 8 ? 'text-green-700' : 'text-amber-600'}`}>
                              {record.hours_worked >= 1 ? `${Math.floor(record.hours_worked)}h ${Math.round((record.hours_worked % 1) * 60)}m` : `${Math.round(record.hours_worked * 60)}m`}
                            </span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        {/* GPS column */}
                        <td className="p-3 text-center">
                          {record.status === 'not_checked_in' ? (
                            <span className="text-gray-300">-</span>
                          ) : record.gps_fallback ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-600 border border-amber-200" title="GPS fallback used">
                              Manual
                            </span>
                          ) : record.check_in_validation === 'within_geofence' ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-600 border border-green-200" title={`${record.check_in_distance_meters ? Math.round(record.check_in_distance_meters) + 'm' : 'Verified'}`}>
                              {record.check_in_distance_meters ? `${Math.round(record.check_in_distance_meters)}m` : '✓'}
                            </span>
                          ) : record.check_in_validation ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200" title={record.check_in_validation}>
                              {record.check_in_distance_meters ? `${Math.round(record.check_in_distance_meters)}m` : record.check_in_validation.replace(/_/g, ' ')}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        {/* Photo column */}
                        <td className="p-3 text-center">
                          {record.status === 'not_checked_in' ? (
                            <span className="text-gray-300">-</span>
                          ) : record.photo_fallback_used ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-600 border border-amber-200" title="Photo fallback">
                              Text
                            </span>
                          ) : record.check_in_photo_url ? (
                            <div className="flex items-center justify-center gap-1">
                              <a href={record.check_in_photo_url} target="_blank" rel="noreferrer"
                                className="w-7 h-7 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100 transition-colors" title="Check-in photo">
                                <Camera className="h-3.5 w-3.5 text-emerald-600" />
                              </a>
                              {record.check_out_photo_url && (
                                <a href={record.check_out_photo_url} target="_blank" rel="noreferrer"
                                  className="w-7 h-7 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-colors" title="Check-out photo">
                                  <Camera className="h-3.5 w-3.5 text-blue-600" />
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        {/* Flags column (replaces Strikes) */}
                        {isAdmin && (
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {strikes > 0 && (
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold ${
                                  isLocked ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-amber-100 text-amber-800 border border-amber-300'
                                }`}>
                                  <ShieldAlert className="h-3 w-3" />
                                  {strikes}{isLocked && ' Locked'}
                                </span>
                              )}
                              {record.flagged_for_review && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-600 border border-orange-200" title={record.flag_reason || 'Flagged'}>
                                  <AlertTriangle className="h-3 w-3" />
                                </span>
                              )}
                              {record.missed_checkout && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                                  Miss
                                </span>
                              )}
                              {!hasFlags && strikes === 0 && record.status !== 'not_checked_in' && (
                                <span className="text-green-500 text-xs">✓ Clean</span>
                              )}
                              {record.status === 'not_checked_in' && (
                                <span className="text-gray-300 text-xs">-</span>
                              )}
                            </div>
                          </td>
                        )}
                        {isTeamView && (
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {isAdmin && isLocked && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setUnblockTarget(record)}
                                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                >
                                  <Unlock className="h-3 w-3 mr-1" />
                                  Unblock
                                </Button>
                              )}
                              {(record.user_role === 'team_leader' || record.user_role === 'fitness_manager' || record.role === 'team_leader' || record.role === 'fitness_manager') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setBh0MarkTarget({
                                      user_id: record.user_id || record.linked_id,
                                      user_name: record.user_name || record.name,
                                    });
                                    setBh0MarkDates([]);
                                    setBh0MarkReason('');
                                    setBh0DateInput('');
                                    setBh0MarkModalOpen(true);
                                  }}
                                  className="text-xs border-red-300 text-red-700 hover:bg-red-50"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Mark BH0
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  // ═════════════════════════════════════════════════════════════════════
  // Main render
  // ═════════════════════════════════════════════════════════════════════
  return (
    <div className="p-6 space-y-6">
      {/* TL/FM: only see personal calendar */}
      {!isTeamView && (
        <>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="h-6 w-6 text-[#7C1D3E]" />
              Attendance Report
            </h1>
            <p className="text-gray-500 text-sm mt-1">Your monthly attendance overview</p>
          </div>
          {renderPersonalCalendar()}
        </>
      )}

      {/* Admin/Manager: team table + personal calendar below */}
      {isTeamView && (
        <>
          {/* Tab switcher */}
          <div className="flex gap-2 border-b border-gray-200 pb-2">
            <button
              onClick={() => setAdminTab('team')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                adminTab === 'team' ? 'bg-[#7C1D3E] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                Attendance Report
              </span>
            </button>
            <button
              onClick={() => setAdminTab('bh0')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                adminTab === 'bh0' ? 'bg-[#7C1D3E] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Shield className="h-4 w-4" />
                BH0 Payroll
              </span>
            </button>
          </div>

          {adminTab === 'team' && (
            <>
              {renderTeamView()}

              {/* ── Regularization Review Section ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileEdit className="h-5 w-5 text-blue-600" />
                Regularization Requests
                {pendingRegs.length > 0 && (
                  <span className="text-sm bg-blue-100 text-blue-700 px-2.5 py-0.5 rounded-full font-semibold">
                    {pendingRegs.length} pending
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {regsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">Loading requests...</span>
                </div>
              ) : pendingRegs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileEdit className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No pending regularization requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRegs.map((req) => {
                    const reqId = req.id || req._id;
                    const isActing = regActionLoading === reqId;
                    const isRejecting = rejectingId === reqId;
                    return (
                      <div
                        key={reqId}
                        className="border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                      >
                        {/* Header row: user info + status */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-800">{req.user_name || 'Unknown'}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 rounded">
                                {getRoleDisplay(req.user_role)}
                              </span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                req.status === 'pending_manager'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {req.status === 'pending_manager' ? 'Awaiting Manager' : 'Awaiting Admin'}
                              </span>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">{req.date}</span>
                        </div>

                        {/* Details grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                          <div>
                            <p className="text-xs text-gray-500">Location</p>
                            <p className="font-medium flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              {req.location_name || 'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Claimed Times</p>
                            <p className="font-medium flex items-center gap-1">
                              <Clock className="h-3 w-3 text-gray-400" />
                              {req.claimed_check_in} - {req.claimed_check_out}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500">Reason</p>
                            <p className="text-sm text-gray-700">{req.reason || '-'}</p>
                          </div>
                        </div>

                        {/* Photo proofs */}
                        <div className="flex flex-wrap gap-3 mb-3">
                          {req.check_in_proof_url && (
                            <div className="text-center">
                              <p className="text-[10px] text-gray-500 mb-1">Check-in Proof</p>
                              <img
                                src={req.check_in_proof_url}
                                alt="Check-in proof"
                                className="w-16 h-16 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setRegPhotoPreview(req.check_in_proof_url)}
                              />
                            </div>
                          )}
                          {req.check_out_proof_url && (
                            <div className="text-center">
                              <p className="text-[10px] text-gray-500 mb-1">Check-out Proof</p>
                              <img
                                src={req.check_out_proof_url}
                                alt="Check-out proof"
                                className="w-16 h-16 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setRegPhotoPreview(req.check_out_proof_url)}
                              />
                            </div>
                          )}
                          {(req.additional_proof_urls || []).map((url, i) => (
                            <div key={i} className="text-center">
                              <p className="text-[10px] text-gray-500 mb-1">Evidence {i + 1}</p>
                              <img
                                src={url}
                                alt={`Evidence ${i + 1}`}
                                className="w-16 h-16 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setRegPhotoPreview(url)}
                              />
                            </div>
                          ))}
                        </div>

                        {/* Reject reason input (when rejecting) */}
                        {isRejecting && (
                          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <label className="block text-sm font-medium text-red-800 mb-1">
                              Rejection Reason
                            </label>
                            <textarea
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Explain why this request is being rejected..."
                              rows={2}
                              className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none resize-none"
                            />
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          {isRejecting ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                disabled={isActing}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleRegReject(reqId)}
                                disabled={isActing || !rejectReason.trim()}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                {isActing ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                ) : (
                                  <XCircle className="h-3.5 w-3.5 mr-1" />
                                )}
                                Confirm Reject
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleRegApprove(reqId)}
                                disabled={isActing}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                {isActing ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                ) : (
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                )}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setRejectingId(reqId); setRejectReason(''); }}
                                disabled={isActing}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── BH0 Appeals Review Section (admin only) ── */}
          {isAdmin && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-red-600" />
                  BH0 Appeals
                  {bh0Appeals.length > 0 && (
                    <span className="text-sm bg-red-100 text-red-700 px-2.5 py-0.5 rounded-full font-semibold">
                      {bh0Appeals.length} pending
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bh0AppealsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Loading appeals...</span>
                  </div>
                ) : bh0Appeals.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No pending BH0 appeals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bh0Appeals.map((appeal) => {
                      const appealId = appeal.id;
                      const isResolving = bh0ResolvingId === appealId;
                      return (
                        <div
                          key={appealId}
                          className="border border-red-200 rounded-xl p-4 bg-red-50/30 hover:shadow-sm transition-shadow"
                        >
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-semibold text-gray-800">{appeal.user_name || 'Unknown'}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 rounded">
                                  {getRoleDisplay(appeal.user_role)}
                                </span>
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  Appealed
                                </span>
                              </div>
                            </div>
                            <span className="text-sm font-medium text-gray-600">{appeal.date}</span>
                          </div>

                          {/* Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                            <div>
                              <p className="text-xs text-gray-500">Manager's Reason for BH0</p>
                              <p className="text-sm text-gray-700 font-medium">{appeal.reason || '-'}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                Marked by {appeal.marked_by_name} on {appeal.marked_at ? format(new Date(appeal.marked_at), 'MMM d, yyyy') : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">TL/FM's Appeal</p>
                              <p className="text-sm text-gray-700 font-medium">{appeal.appeal_reason || '-'}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                Submitted {appeal.appeal_submitted_at ? format(new Date(appeal.appeal_submitted_at), 'MMM d, yyyy h:mm a') : '-'}
                              </p>
                            </div>
                          </div>

                          {/* History timeline */}
                          {appeal.history && appeal.history.length > 0 && (
                            <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-500 mb-1">History</p>
                              <div className="space-y-1">
                                {appeal.history.map((h, hi) => (
                                  <div key={hi} className="flex items-center gap-2 text-xs text-gray-600">
                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                                    <span className="font-medium">{h.action}</span>
                                    <span>by {h.by_name}</span>
                                    {h.at && <span className="text-gray-400">{format(new Date(h.at), 'MMM d, h:mm a')}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Resolve notes input */}
                          {isResolving && (
                            <div className={`mb-3 p-3 rounded-lg border ${
                              bh0ResolveDecision === 'reverse' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}>
                              <label className={`block text-sm font-medium mb-1 ${
                                bh0ResolveDecision === 'reverse' ? 'text-green-800' : 'text-red-800'
                              }`}>
                                {bh0ResolveDecision === 'reverse' ? 'Reversal' : 'Uphold'} Notes (optional)
                              </label>
                              <textarea
                                value={bh0ResolveNotes}
                                onChange={(e) => setBh0ResolveNotes(e.target.value)}
                                placeholder={bh0ResolveDecision === 'reverse'
                                  ? 'Explain why BH0 is being reversed...'
                                  : 'Explain why BH0 is upheld...'
                                }
                                rows={2}
                                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none resize-none ${
                                  bh0ResolveDecision === 'reverse'
                                    ? 'border-green-300 focus:ring-2 focus:ring-green-300'
                                    : 'border-red-300 focus:ring-2 focus:ring-red-300'
                                }`}
                              />
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                            {isResolving ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { setBh0ResolvingId(null); setBh0ResolveDecision(null); setBh0ResolveNotes(''); }}
                                  disabled={bh0ResolveLoading}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleBh0ResolveAppeal(appealId, bh0ResolveDecision)}
                                  disabled={bh0ResolveLoading}
                                  className={bh0ResolveDecision === 'reverse'
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                  }
                                >
                                  {bh0ResolveLoading ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                  ) : bh0ResolveDecision === 'reverse' ? (
                                    <Check className="h-3.5 w-3.5 mr-1" />
                                  ) : (
                                    <XCircle className="h-3.5 w-3.5 mr-1" />
                                  )}
                                  Confirm {bh0ResolveDecision === 'reverse' ? 'Reverse' : 'Uphold'}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => { setBh0ResolvingId(appealId); setBh0ResolveDecision('reverse'); setBh0ResolveNotes(''); }}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="h-3.5 w-3.5 mr-1" />
                                  Reverse BH0
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => { setBh0ResolvingId(appealId); setBh0ResolveDecision('uphold'); setBh0ResolveNotes(''); }}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  <XCircle className="h-3.5 w-3.5 mr-1" />
                                  Uphold BH0
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

            </>
          )}

          {adminTab === 'bh0' && (
            <BH0PayrollTab
              onMarkBH0={(userId, userName) => {
                setBh0MarkTarget({ user_id: userId, user_name: userName });
                setBh0MarkDates([]);
                setBh0MarkReason('');
                setBh0DateInput('');
                setBh0MarkModalOpen(true);
              }}
              onRefreshRef={bh0TabRefreshRef}
            />
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-bold text-gray-700 mb-4">Your Personal Attendance</h2>
            {renderPersonalCalendar()}
          </div>
        </>
      )}

      {/* Day detail modal */}
      <DayDetailModal
        day={selectedDay}
        record={selectedDayRecord}
        onClose={() => { setSelectedDay(null); setSelectedDayRecord(null); }}
      />

      {/* Unblock confirmation dialog */}
      <UnblockDialog
        user={unblockTarget}
        onConfirm={handleUnblock}
        onCancel={() => setUnblockTarget(null)}
      />

      {/* Mark BH0 Modal */}
      {bh0MarkModalOpen && bh0MarkTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBh0MarkModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <Shield className="h-5 w-5 text-red-700" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Mark Billing Hour Zero</h3>
                  <p className="text-sm text-gray-500">for <strong>{bh0MarkTarget.user_name}</strong></p>
                </div>
              </div>
              <button onClick={() => setBh0MarkModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Date picker */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Dates (today or past)</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={bh0DateInput}
                    onChange={(e) => setBh0DateInput(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addBh0Date(bh0DateInput)}
                    disabled={!bh0DateInput}
                  >
                    Add
                  </Button>
                </div>
                {/* Selected dates */}
                {bh0MarkDates.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bh0MarkDates.map((d) => (
                      <span
                        key={d}
                        className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-2 py-1 rounded-full"
                      >
                        {d}
                        <button
                          onClick={() => removeBh0Date(d)}
                          className="hover:bg-red-100 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {bh0MarkDates.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No dates selected yet</p>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-gray-400">(min 10 characters)</span>
                </label>
                <textarea
                  value={bh0MarkReason}
                  onChange={(e) => setBh0MarkReason(e.target.value)}
                  placeholder="Explain why BH0 is being applied (e.g., non-completion of SOP for assigned gyms)..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none resize-none"
                />
                <p className="text-xs text-gray-400 mt-0.5">{bh0MarkReason.trim().length}/10 min characters</p>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setBh0MarkModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleBh0Mark}
                  disabled={bh0MarkLoading || bh0MarkDates.length === 0 || bh0MarkReason.trim().length < 10}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {bh0MarkLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Mark BH0 ({bh0MarkDates.length} date{bh0MarkDates.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regularization photo preview modal */}
      {regPhotoPreview && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setRegPhotoPreview(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <img
              src={regPhotoPreview}
              alt="Proof photo"
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            />
            <button
              className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 text-gray-700 shadow-lg"
              onClick={() => setRegPhotoPreview(null)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
